import React, { useState } from "react";
import { Button, Popover, Select, Tag, message } from "antd";
import {
  ArrowLeft,
  Printer,
  Download,
  RotateCcw,
  BarChart3,
  SlidersHorizontal,
  Store,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import html2pdf from "html2pdf.js";
import { useReportStore } from "../../Page/reports/ReportStoreContext";
import { useSystemConfig } from "../../Hook/useSystemConfig";
import { renderBusinessReport } from "../templates";
import { drawerTitle } from "../AppUi";
import { ResizableDrawer } from "../ResizableDrawer/ResizableDrawer";

interface ReportPageProps {
  title: string;
  description: string;
  icon: React.ReactNode;
  backPath: string;
  stats?: Array<{ title: string; value: string; colorClass?: string }>;
  onExportCSV?: () => void;
  onExportPDF?: () => void;
  isExportDisabled?: boolean;
  onRefresh?: () => void;
  filterControls?: React.ReactNode;
  isCustomRender?: boolean;
  children?: React.ReactNode;
}

export const ReportPage: React.FC<ReportPageProps> = ({
  title,
  description,
  icon,
  backPath,
  stats,
  onExportCSV,
  onExportPDF,
  isExportDisabled = false,
  onRefresh,
  filterControls,
  isCustomRender = false,
  children,
}) => {
  const navigate = useNavigate();
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isExportingPDF, setIsExportingPDF] = useState(false);
  const { stores, isLoadingStores, selectedStoreIds, setSelectedStoreIds } =
    useReportStore();
  const { storeName, appName, address, phone, email } = useSystemConfig();

  const storeOptions = stores.map((store) => ({
    value: store.id,
    label: store.name,
  }));

  const storeFilterEmpty = selectedStoreIds.length === 0;

  const handleDownloadPDF = () => {
    if (onExportPDF) {
      onExportPDF();
      return;
    }

    const reportElement = document.getElementById("report-content-container");
    if (!reportElement) {
      message.error("Report content not found to export.");
      return;
    }

    setIsExportingPDF(true);

    try {
      const clone = reportElement.cloneNode(true) as HTMLElement;

      // Clean up interactive and pagination UI elements
      clone
        .querySelectorAll(
          [
            ".no-print",
            ".ant-pagination",
            ".ant-table-pagination",
            ".ant-pagination-options",
            ".ant-btn",
            ".ant-select",
            ".ant-input-search",
            ".ant-input-affix-wrapper",
            "button",
          ].join(","),
        )
        .forEach((el) => el.remove());

      // Format tables for clean PDF printing without horizontal cutoffs
      clone.querySelectorAll("table").forEach((table) => {
        const t = table as HTMLElement;
        t.style.width = "100%";
        t.style.tableLayout = "auto";
        t.style.fontSize = "10px";
        t.style.borderCollapse = "collapse";
        t.style.margin = "0";
      });

      clone.querySelectorAll("th, td").forEach((cell) => {
        const element = cell as HTMLElement;
        element.style.padding = "6px 8px";
        element.style.border = "1px solid #cbd5e1";
        element.style.whiteSpace = "normal";
        element.style.wordBreak = "break-word";
      });

      clone.querySelectorAll("th").forEach((th) => {
        const element = th as HTMLElement;
        element.style.backgroundColor = "#f1f5f9";
        element.style.color = "#1e293b";
        element.style.fontWeight = "bold";
        element.style.textTransform = "uppercase";
        element.style.fontSize = "9.5px";
      });

      // Remove scroll containers clipping content
      clone
        .querySelectorAll(
          ".ant-table-body, .ant-table-content, .overflow-x-auto, .overflow-auto",
        )
        .forEach((container) => {
          const element = container as HTMLElement;
          element.style.overflow = "visible";
          element.style.width = "100%";
          element.style.maxWidth = "100%";
        });

      // Clean card shadows
      clone
        .querySelectorAll(".ant-card, .bg-white, .shadow-sm")
        .forEach((card) => {
          const element = card as HTMLElement;
          element.style.boxShadow = "none";
          element.style.border = "1px solid #e2e8f0";
          element.style.borderRadius = "6px";
        });

      // Determine orientation based on column count
      const thCount = clone.querySelectorAll("table th").length;
      const orientation: "portrait" | "landscape" =
        thCount > 5 ? "landscape" : "portrait";

      const selectedStoresLabel =
        selectedStoreIds.length > 0
          ? selectedStoreIds
              .map((id) => stores.find((s) => s.id === id)?.name || id)
              .join(", ")
          : "All Stores";

      const fullHtml = renderBusinessReport({
        title,
        description,
        storeName: storeName || appName || "POS SYSTEM",
        address,
        phone,
        email,
        generatedAt: dayjs().format("YYYY-MM-DD HH:mm"),
        selectedStoresLabel,
        stats,
        reportHtmlContent: clone.innerHTML,
        orientation,
        isForPrint: true,
      });

      const container = document.createElement("div");
      container.innerHTML = fullHtml;

      const sanitizeFileName = (str: string) =>
        str.replace(/[<>:"/\\|?*\x00-\x1f]/g, "_").trim();
      const fileName = `${sanitizeFileName(title)}_${dayjs().format("YYYYMMDD_HHmm")}.pdf`;

      html2pdf()
        .from(container)
        .set({
          filename: fileName,
          margin: orientation === "landscape" ? [6, 6, 6, 6] : [8, 8, 8, 8],
          image: { type: "jpeg", quality: 0.98 },
          html2canvas: {
            scale: 2,
            useCORS: true,
            logging: false,
            windowWidth: orientation === "landscape" ? 1200 : 900,
          },
          jsPDF: {
            unit: "mm",
            format: "a4",
            orientation: orientation,
          },
        })
        .save()
        .then(() => {
          setIsExportingPDF(false);
          message.success("Report PDF downloaded successfully.");
        })
        .catch((err: any) => {
          console.error("PDF export failed:", err);
          setIsExportingPDF(false);
          message.error("Failed to generate PDF report.");
        });
    } catch (err: any) {
      console.error("PDF generation error:", err);
      setIsExportingPDF(false);
      message.error("Failed to generate PDF report.");
    }
  };

  return (
    <div className="p-6 min-h-screen bg-gray-50 flex flex-col gap-6 printable-report">
      {/* Detail Header Control Bar */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between border-b border-gray-200 pb-4 no-print">
        <div className="flex items-center gap-3.5">
          <Button
            type="text"
            icon={<ArrowLeft className="w-5 h-5 text-gray-600" />}
            onClick={() => navigate(-1)}
            className="flex items-center justify-center p-1.5 hover:bg-gray-150 rounded-lg no-print"
          />
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-white rounded-xl shadow-sm border border-gray-150">
              {icon}
            </div>
            <div className="flex flex-col gap-0.5">
              <div className="flex items-center gap-2">
                <h1 className="text-base font-bold text-gray-900 leading-tight">
                  {title}
                </h1>
                {!isCustomRender && stats && stats.length > 0 && (
                  <Popover
                    placement="bottom"
                    trigger="hover"
                    content={
                      <div className="flex flex-col gap-2.5 min-w-[230px]">
                        {stats.map((stat, idx) => (
                          <div
                            key={idx}
                            className="flex items-center justify-between gap-6 font-sans"
                          >
                            <span className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">
                              {stat.title}
                            </span>
                            <span
                              className={`text-sm font-black ${stat.colorClass || "text-gray-900"}`}
                            >
                              {stat.value}
                            </span>
                          </div>
                        ))}
                      </div>
                    }
                  >
                    <Button
                      shape="circle"
                      size="small"
                      icon={
                        <BarChart3 className="w-3.5 h-3.5 text-indigo-600" />
                      }
                      className="flex items-center justify-center border-gray-200 no-print"
                    />
                  </Popover>
                )}
              </div>
              <p className="text-xs text-gray-500">{description}</p>
            </div>
          </div>
        </div>

        {/* Global actions */}
        <div className="flex items-center gap-2 self-end sm:self-auto">
          {!isCustomRender && (
            <Button
              icon={<SlidersHorizontal className="w-4 h-4" />}
              className="text-xs font-semibold hover:border-gray-300 rounded-lg"
              onClick={() => setIsFilterOpen(true)}
            >
              Filters
              {selectedStoreIds.length > 0 && (
                <span className="ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-indigo-600 text-white text-[10px] font-bold">
                  {selectedStoreIds.length}
                </span>
              )}
            </Button>
          )}
          <Button
            type="primary"
            icon={<Download className="w-4 h-4" />}
            className="bg-indigo-600 border-none text-xs hover:bg-indigo-700 font-semibold rounded-lg"
            onClick={handleDownloadPDF}
            loading={isExportingPDF}
            disabled={isExportDisabled || (!isCustomRender && storeFilterEmpty)}
          >
            Export PDF
          </Button>

          <Button
            type="primary"
            icon={<Download className="w-4 h-4" />}
            className="bg-indigo-600 border-none text-xs hover:bg-indigo-700 font-semibold rounded-lg"
            onClick={onExportCSV}
            loading={isExportingPDF}
            disabled={isExportDisabled || (!isCustomRender && storeFilterEmpty)}
          >
            Export Csv
          </Button>
          {onRefresh && (
            <Button
              shape="circle"
              icon={<RotateCcw className="w-4 h-4 text-gray-500" />}
              onClick={onRefresh}
              className="hover:border-gray-300"
            />
          )}
        </div>
      </div>

      {/* Print view header */}
      {!isCustomRender && (
        <div className="hidden print:block text-center border-b pb-4 mb-4">
          <h1 className="text-2xl font-black text-gray-955 uppercase leading-tight">
            {title}
          </h1>
          <p className="text-xs text-gray-600 font-medium mt-1">
            Generated: {dayjs().format("YYYY-MM-DD HH:mm")}
          </p>
        </div>
      )}

      {/* Interactive Filter Drawer */}
      {!isCustomRender && (
        <ResizableDrawer
          title={drawerTitle("Filters")}
          placement="right"
          width={380}
          open={isFilterOpen}
          onClose={() => setIsFilterOpen(false)}
          className="no-print"
        >
          <div className="flex flex-col gap-4 font-sans">
            <div className="flex flex-col gap-1.5">
              <div className="flex items-center gap-1.5 text-xs font-semibold text-gray-600">
                <Store className="w-3.5 h-3.5" />
                Stores
                <Tag color="red" className="ml-auto font-bold text-[10px]">
                  Required
                </Tag>
              </div>
              <Select
                mode="multiple"
                loading={isLoadingStores}
                placeholder="Select at least one store"
                options={storeOptions}
                value={selectedStoreIds}
                onChange={setSelectedStoreIds}
                className="w-full"
                maxTagCount="responsive"
              />
              {storeFilterEmpty ? (
                <span className="text-[10px] text-rose-500 font-medium">
                  Select at least one store- reports are always filtered by
                  store.
                </span>
              ) : (
                <span className="text-[10px] text-gray-400">
                  <Tag color="indigo" className="mr-1">
                    {selectedStoreIds.length}
                  </Tag>
                  store(s) selected- data is restricted to these stores.
                </span>
              )}
            </div>
            {filterControls}
          </div>
        </ResizableDrawer>
      )}

      {/* Main Content */}
      {!isCustomRender && storeFilterEmpty ? (
        <div className="bg-white rounded-2xl border border-gray-200/80 shadow-sm p-12 flex flex-col items-center gap-3 no-print">
          <div className="p-3 rounded-full bg-indigo-50">
            <Store className="w-8 h-8 text-indigo-600" />
          </div>
          <p className="text-sm font-bold text-gray-800">
            Store filter is required
          </p>
          <p className="text-xs text-gray-400 text-center max-w-sm">
            Select at least one store from the Filters section to view this
            report. Data is always restricted to the selected store(s).
          </p>
          <Button
            type="primary"
            className="bg-indigo-600 border-none text-xs hover:bg-indigo-700 font-semibold rounded-lg"
            onClick={() => setIsFilterOpen(true)}
          >
            Open Filters
          </Button>
        </div>
      ) : (
        <div id="report-content-container" className="w-full">
          {children}
        </div>
      )}
    </div>
  );
};
